import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { claimPendingInviteAgreement, setPendingInviteAgreement } from '@/lib/invite';

const wallpaper = require('../../../assets/wallpaper.jpg');
const logo = require('../../../assets/logo.png');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.88)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

type AgreementPreview = {
  id: string;
  title: string;
  description: string | null;
  amount: number | null;
  currency: string;
  amount_unit: string;
  creator_name: string;
  creator_id: string;
};

function formatAmt(amount: number | null, currency: string, unit: string, creatorName: string, youLabel: string): string | null {
  if (amount == null) return null;
  if (unit === 'percent') {
    // Ο προσκεκλημένος είναι ο αντισυμβαλλόμενος — το δικό του μερίδιο είναι το
    // συμπληρωματικό ποσοστό, όχι το ποσοστό του δημιουργού που είναι αποθηκευμένο.
    const yourShare = Math.round((100 - amount) * 100) / 100;
    return `${creatorName}: ${amount}% · ${youLabel}: ${yourShare}%`;
  }
  return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
}

export default function AgreementInviteScreen() {
  const { t } = useTranslation();
  // `code`: ο 6ψήφιος κωδικός, όταν ο χρήστης έρχεται από τη σελίδα
  // πρόσκλησης όπου τον πληκτρολόγησε ήδη. Τον κρατάμε ώστε μετά την εγγραφή
  // να δηλωθεί αντισυμβαλλόμενος ακόμα κι αν εγγραφεί με άλλο email.
  const { agreement: agreementId, code: inviteCode } = useLocalSearchParams<{ agreement?: string; code?: string }>();
  const { session } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';

  const [preview, setPreview] = useState<AgreementPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agreementId) { setLoading(false); return; }
    void fetch(`${supabaseUrl}/functions/v1/agreement-invite-preview/${agreementId}`)
      .then((r) => r.json())
      .then(async (data) => {
        if (data?.error) {
          setError(t('invite.agreement_notFound'));
          setLoading(false);
          return;
        }
        if (session) {
          // Ήδη συνδεδεμένος → δήλωσέ τον αντισυμβαλλόμενο (αν έχουμε κωδικό)
          // και πήγαινε στη συμφωνία.
          await setPendingInviteAgreement(agreementId, inviteCode);
          await claimPendingInviteAgreement(agreementId);
          router.replace({ pathname: '/(app)/agreements/[id]', params: { id: agreementId } });
          return;
        }
        await setPendingInviteAgreement(agreementId, inviteCode);
        setPreview(data as AgreementPreview);
        setLoading(false);
      })
      .catch(() => {
        setError(t('invite.agreement_loadError'));
        setLoading(false);
      });
  }, [agreementId, inviteCode, supabaseUrl, session, router]);

  function goRegister() { router.replace('/(auth)/register'); }
  function goLogin() { router.replace('/(auth)/login'); }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <View
        style={[
          styles.container,
          { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
        ]}
      >
        <View style={styles.logoWrap}>
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        </View>

        {loading ? (
          <View style={[styles.card, styles.center]}>
            <ActivityIndicator color={GOLD} size="large" />
          </View>
        ) : error ? (
          <View style={styles.card}>
            <Text style={styles.errorText}>{error}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        ) : preview ? (
          <View style={styles.card}>
            <View style={styles.inviteHeader}>
              <Ionicons name="document-text-outline" size={28} color={GOLD} />
              <Text style={styles.inviteLabel}>{t('invite.agreement_header')}</Text>
            </View>

            <View style={styles.creatorRow}>
              <View style={styles.avatarFallback}>
                <Text style={styles.avatarInitial}>
                  {preview.creator_name.charAt(0).toUpperCase()}
                </Text>
              </View>
              <Text style={styles.creatorName}>{preview.creator_name}</Text>
            </View>

            <View style={styles.agreementCard}>
              <Text style={styles.agreementTitle}>{preview.title}</Text>
              {!!preview.description && (
                <Text style={styles.agreementDesc} numberOfLines={4}>{preview.description}</Text>
              )}
              {formatAmt(preview.amount, preview.currency, preview.amount_unit, preview.creator_name, t('invite.youLabel')) && (
                <View style={styles.detailItem}>
                  <Ionicons name="cash-outline" size={16} color={GOLD} />
                  <Text style={styles.detailText}>
                    {formatAmt(preview.amount, preview.currency, preview.amount_unit, preview.creator_name, t('invite.youLabel'))}
                  </Text>
                </View>
              )}
            </View>

            <Text style={styles.inviteText}>
              {t('invite.agreement_loginPrompt')}
            </Text>

            <Button title={t('invite.action_registerAndAccept')} icon="create-outline" onPress={goRegister} />
            <View style={styles.divider}>
              <Text style={styles.dividerText}>{t('invite.have_account')}</Text>
            </View>
            <Button title={t('auth.action.login')} variant="secondary" onPress={goLogin} />
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.errorText}>{t('invite.agreement_notFound')}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        )}
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...require('react-native').StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.68)',
  },
  container: {
    // Κεντραρισμένη κάρτα σε μεγάλες οθόνες αντί για τέντωμα.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flex: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
  },
  logoWrap: { alignItems: 'center' },
  logo: { width: 120, height: 120 },
  card: {
    backgroundColor: CARD_BG, borderRadius: radius.xl,
    borderWidth: 1, borderColor: CARD_BORDER, padding: spacing.lg, gap: spacing.md,
  },
  center: { alignItems: 'center', justifyContent: 'center', minHeight: 180 },
  inviteHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  inviteLabel: { color: GOLD, fontSize: fontSize.lg, fontWeight: '700' },
  creatorRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  avatarFallback: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(201,168,67,0.2)', alignItems: 'center', justifyContent: 'center',
  },
  avatarInitial: { color: GOLD, fontSize: fontSize.xl, fontWeight: '700' },
  creatorName: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '600' },
  agreementCard: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: radius.md,
    padding: spacing.md, gap: spacing.sm,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
  },
  agreementTitle: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '700' },
  agreementDesc: { color: TEXT_MUTED, fontSize: fontSize.sm, lineHeight: 19 },
  detailItem: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: spacing.xs },
  detailText: { color: GOLD, fontSize: fontSize.sm, fontWeight: '600' },
  inviteText: { color: TEXT_MUTED, fontSize: fontSize.md, lineHeight: 22, textAlign: 'center' },
  divider: { alignItems: 'center' },
  dividerText: { color: TEXT_MUTED, fontSize: fontSize.sm },
  errorText: { color: '#F87171', fontSize: fontSize.md, textAlign: 'center' },
});
